/* iter101/103 — Segmented broadcast to parents (class / cycle / all) — SMS and/or Push,
 * unpaid-balance reminder mode, scheduling with cancel. */
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Megaphone, PaperPlaneTilt, Clock, Wallet, XCircle, BookmarkSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../lib/api';

const fmtFCFA = (v) => (Number(v) || 0).toLocaleString('fr-FR').replace(/\u202f|,/g, ' ') + ' FCFA';
const fmtDate = (iso) => { const d = new Date(iso); return isNaN(d) ? iso : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); };

const ScheduledList = ({ items, onCancel, isFR }) => {
  if (!items.length) return null;
  return (
    <div className="space-y-1" data-testid="scheduled-list">
      <Label className="text-xs flex items-center gap-1"><Clock size={12} /> {isFR ? 'Envois programmés' : 'Scheduled sends'}</Label>
      <div className="rounded-md border border-[#E2E8F0] divide-y max-h-40 overflow-y-auto">
        {items.map(s => (
          <div key={s.id} className="flex items-center gap-2 px-2 py-1.5 text-xs" data-testid={`scheduled-item-${s.id}`}>
            <span className={`px-1.5 py-0.5 rounded font-medium ${s.status === 'scheduled' ? 'bg-amber-50 text-amber-700' : s.status === 'sent' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {s.status === 'scheduled' ? (isFR ? 'Programmé' : 'Scheduled') : s.status === 'sent' ? (isFR ? 'Envoyé' : 'Sent') : (isFR ? 'Échec' : 'Failed')}
            </span>
            <span className="font-medium whitespace-nowrap">{fmtDate(s.scheduled_at)}</span>
            <span className="text-[#64748B] truncate flex-1" title={s.message}>
              {s.scope === 'all' ? (isFR ? 'Toute l\'école' : 'Whole school') : s.target} · {s.recipients_estimate} {isFR ? 'élèves' : 'students'} · {s.message}
            </span>
            {s.status === 'scheduled' && (
              <button type="button" onClick={() => onCancel(s.id)} data-testid={`scheduled-cancel-${s.id}`}
                className="text-red-600 hover:text-red-700 flex items-center gap-0.5">
                <XCircle size={14} /> {isFR ? 'Annuler' : 'Cancel'}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const SmsBroadcastDialog = ({ open, onOpenChange, isFR = true }) => {
  const [seg, setSeg] = useState(null);
  const [scope, setScope] = useState('class');
  const [target, setTarget] = useState('');
  const [channels, setChannels] = useState({ sms: true, push: false });
  const [title, setTitle] = useState("Message de l'école");
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState('free'); // free | unpaid
  const [minBalance, setMinBalance] = useState('0');
  const [unpaid, setUnpaid] = useState(null);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduled, setScheduled] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState('');
  const [savingTpl, setSavingTpl] = useState(false);

  const loadTemplates = () => api.get('/sms/message-templates').then(r => setTemplates(r.data.items || [])).catch(() => {});
  const applyTemplate = (id) => {
    setTplId(id);
    const t = templates.find(x => x.id === id);
    if (!t) return;
    setMessage(t.message);
    if (t.title) setTitle(t.title);
    if (t.channels?.length) setChannels({ sms: t.channels.includes('sms'), push: t.channels.includes('push') });
    setMode(t.only_unpaid ? 'unpaid' : 'free');
    if (t.only_unpaid) setMinBalance(String(t.min_balance || 0));
  };
  const saveTemplate = async () => {
    const name = window.prompt(isFR ? 'Nom du modèle :' : 'Template name:');
    if (!name || !name.trim()) return;
    setSavingTpl(true);
    try {
      await api.post('/sms/message-templates', { name: name.trim(), message, channels: chosen, title, only_unpaid: mode === 'unpaid', min_balance: Number(minBalance) || 0 });
      toast.success(isFR ? 'Modèle enregistré' : 'Template saved'); loadTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setSavingTpl(false); }
  };
  const deleteTemplate = async () => {
    if (!tplId) return;
    try { await api.delete(`/sms/message-templates/${tplId}`); toast.success(isFR ? 'Modèle supprimé' : 'Template deleted'); setTplId(''); loadTemplates(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const loadScheduled = () => api.get('/sms/scheduled').then(r => setScheduled(r.data.items || [])).catch(() => {});

  useEffect(() => {
    if (!open) return;
    setResult(null);
    api.get('/sms/segments').then(r => setSeg(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || 'Erreur chargement des segments'));
    loadScheduled();
    loadTemplates();
  }, [open]);

  useEffect(() => {
    if (!open || mode !== 'unpaid' || (scope !== 'all' && !target)) { setUnpaid(null); return; }
    const t = setTimeout(() => {
      api.get('/sms/unpaid-preview', { params: { scope, target, min_balance: Number(minBalance) || 0 } })
        .then(r => setUnpaid(r.data)).catch(() => setUnpaid(null));
    }, 300);
    return () => clearTimeout(t);
  }, [open, mode, scope, target, minBalance]);

  const switchMode = (m) => {
    setMode(m);
    if (m === 'unpaid' && seg?.unpaid_default_template && !message.includes('{balance}')) setMessage(seg.unpaid_default_template);
    if (m === 'free' && message === seg?.unpaid_default_template) setMessage('');
  };

  const audience = useMemo(() => {
    if (mode === 'unpaid') return { students: unpaid?.families || 0, phones: unpaid?.phones || 0 };
    if (!seg) return { students: 0, phones: 0 };
    if (scope === 'all') return seg.all;
    if (scope === 'cycle') return seg.cycles.find(c => c.key === target) || { students: 0, phones: 0 };
    for (const c of seg.cycles) { const k = c.classes.find(x => x.name === target); if (k) return k; }
    return { students: 0, phones: 0 };
  }, [seg, scope, target, mode, unpaid]);

  const toggle = (k) => setChannels(p => ({ ...p, [k]: !p[k] }));
  const chosen = Object.keys(channels).filter(k => channels[k]);
  const canSend = message.trim() && chosen.length > 0 && (scope === 'all' || target) && (!scheduleOn || scheduledAt);

  const send = async () => {
    setSending(true);
    try {
      const body = {
        scope, target: scope === 'all' ? '' : target, message: message.trim(), channels: chosen, title,
        only_unpaid: mode === 'unpaid', min_balance: Number(minBalance) || 0,
      };
      if (scheduleOn && scheduledAt) body.scheduled_at = new Date(scheduledAt).toISOString();
      const { data } = await api.post('/sms/broadcast', body);
      if (data.scheduled) {
        toast.success(isFR ? `Envoi programmé le ${fmtDate(data.scheduled_at)} (${data.recipients} élèves)` : `Scheduled for ${fmtDate(data.scheduled_at)}`);
        setResult(null); loadScheduled();
      } else {
        setResult(data);
        toast.success(isFR ? `Envoi terminé : ${data.sms_sent} SMS envoyés, ${data.push_sent} push` : `Done: ${data.sms_sent} SMS sent, ${data.push_sent} push`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isFR ? "Erreur d'envoi" : 'Send error'));
    } finally { setSending(false); }
  };

  const cancelScheduled = async (id) => {
    try { await api.delete(`/sms/scheduled/${id}`); toast.success(isFR ? 'Envoi annulé' : 'Cancelled'); loadScheduled(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const minLocal = new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="broadcast-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <Megaphone size={20} weight="duotone" className="text-[#0052FF]" />
            {isFR ? 'Message groupé aux parents' : 'Group message to parents'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex rounded-md border border-[#E2E8F0] p-0.5 bg-slate-50 text-sm" data-testid="broadcast-mode">
            {[['free', isFR ? 'Message libre' : 'Free message', Megaphone], ['unpaid', isFR ? 'Rappel impayés' : 'Unpaid reminder', Wallet]].map(([k, l, I]) => (
              <button key={k} type="button" onClick={() => switchMode(k)} data-testid={`broadcast-mode-${k}`}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded px-3 py-1.5 transition-colors ${mode === k ? 'bg-white shadow text-[#0F172A] font-medium' : 'text-[#64748B]'}`}>
                <I size={14} /> {l}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{isFR ? 'Segment' : 'Segment'}</Label>
              <Select value={scope} onValueChange={v => { setScope(v); setTarget(''); }}>
                <SelectTrigger data-testid="broadcast-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="class">{isFR ? 'Une classe' : 'One class'}</SelectItem>
                  <SelectItem value="cycle">{isFR ? 'Un niveau (cycle)' : 'One cycle'}</SelectItem>
                  <SelectItem value="all">{isFR ? "Toute l'école" : 'Whole school'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scope !== 'all' && (
              <div className="space-y-1">
                <Label>{scope === 'class' ? (isFR ? 'Classe' : 'Class') : (isFR ? 'Niveau' : 'Cycle')}</Label>
                <Select value={target} onValueChange={setTarget}>
                  <SelectTrigger data-testid="broadcast-target"><SelectValue placeholder={isFR ? 'Choisir…' : 'Select…'} /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {scope === 'cycle'
                      ? (seg?.cycles || []).map(c => <SelectItem key={c.key} value={c.key}>{c.label} ({c.students})</SelectItem>)
                      : (seg?.cycles || []).flatMap(c => c.classes.map(k => <SelectItem key={k.name} value={k.name}>{k.name} — {c.label} ({k.students})</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {mode === 'unpaid' && (
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 space-y-2" data-testid="unpaid-panel">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Label className="whitespace-nowrap">{isFR ? 'Solde minimum (FCFA)' : 'Minimum balance (FCFA)'}</Label>
                <Input type="number" min="0" step="5000" value={minBalance} onChange={e => setMinBalance(e.target.value)}
                  className="w-36 h-8" data-testid="unpaid-min-balance" />
                <span className="ml-auto text-xs text-amber-800" data-testid="unpaid-summary">
                  {unpaid ? <>{isFR ? 'Familles en retard :' : 'Late families:'} <b>{unpaid.families}</b> · {isFR ? 'Total dû :' : 'Total due:'} <b>{fmtFCFA(unpaid.total_due)}</b> · <b>{unpaid.phones}</b> {isFR ? 'numéros' : 'phones'}</> : (isFR ? 'Choisissez une cible…' : 'Pick a target…')}
                </span>
              </div>
              {unpaid?.items?.length > 0 && (
                <div className="max-h-28 overflow-y-auto text-xs divide-y divide-amber-100">
                  {unpaid.items.slice(0, 30).map(i => (
                    <div key={i.id} className="flex justify-between py-0.5">
                      <span className={i.has_phone ? '' : 'text-[#94A3B8]'}>{i.name} <span className="text-[#94A3B8]">({i.class_name})</span></span>
                      <span className="font-medium">{fmtFCFA(i.balance)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4 p-3 rounded-md bg-slate-50 border border-slate-200 text-sm">
            <span className="font-medium">{isFR ? 'Canaux :' : 'Channels:'}</span>
            <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={channels.sms} onCheckedChange={() => toggle('sms')} data-testid="broadcast-channel-sms" /> SMS</label>
            <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={channels.push} onCheckedChange={() => toggle('push')} data-testid="broadcast-channel-push" /> Push</label>
            <span className="ml-auto text-xs text-[#64748B]" data-testid="broadcast-audience">
              {isFR ? 'Audience :' : 'Audience:'} <b>{audience.students}</b> {isFR ? 'élèves' : 'students'} · <b>{audience.phones}</b> {isFR ? 'numéros' : 'phones'}
            </span>
          </div>

          {channels.push && (
            <div className="space-y-1">
              <Label>{isFR ? 'Titre (push)' : 'Title (push)'}</Label>
              <Input value={title} onChange={e => setTitle(e.target.value.slice(0, 80))} data-testid="broadcast-title" />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="broadcast-templates">
            <BookmarkSimple size={16} className="text-[#0052FF]" />
            <select value={tplId} onChange={e => applyTemplate(e.target.value)} data-testid="broadcast-template-select"
              className="h-8 rounded-md border border-[#E2E8F0] px-2 text-sm bg-white min-w-[200px]">
              <option value="">{isFR ? 'Modèle enregistré…' : 'Saved template…'}</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {tplId && (
              <Button size="sm" variant="ghost" className="h-8 text-red-600" onClick={deleteTemplate} data-testid="broadcast-template-delete"><Trash size={14} /></Button>
            )}
            <Button size="sm" variant="outline" className="h-8 ml-auto" onClick={saveTemplate} disabled={!message.trim() || savingTpl} data-testid="broadcast-template-save">
              <BookmarkSimple size={14} className="mr-1" /> {isFR ? 'Enregistrer comme modèle' : 'Save as template'}
            </Button>
          </div>

          <div className="space-y-1">
            <Label>{isFR ? 'Message' : 'Message'}</Label>
            <textarea value={message} rows={4} onChange={e => setMessage(e.target.value.slice(0, 320))} data-testid="broadcast-message"
              placeholder={isFR ? 'Chers parents de {first_name} ({class_name})…' : 'Dear parents of {first_name}…'}
              className="w-full rounded-md border border-[#E2E8F0] p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF]/40" />
            <div className="flex items-center justify-between text-[11px] text-[#94A3B8]">
              <div className="flex flex-wrap gap-1">
                {(seg?.variables || []).filter(v => mode === 'unpaid' || v !== '{balance}').map(v => (
                  <button key={v} type="button" onClick={() => setMessage(p => p + v)} data-testid={`broadcast-var-${v.slice(1, -1)}`}
                    className="bg-slate-100 hover:bg-slate-200 rounded px-1.5 py-0.5 font-mono text-slate-700">{v}</button>
                ))}
              </div>
              <span>{message.length}/320</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 p-3 rounded-md border border-[#E2E8F0] text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={scheduleOn} onCheckedChange={() => setScheduleOn(v => !v)} data-testid="broadcast-schedule-toggle" />
              <Clock size={14} /> {isFR ? 'Programmer l\'envoi' : 'Schedule send'}
            </label>
            {scheduleOn && (
              <Input type="datetime-local" min={minLocal} value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
                className="w-56 h-8" data-testid="broadcast-schedule-at" />
            )}
          </div>

          <ScheduledList items={scheduled} onCancel={cancelScheduled} isFR={isFR} />

          {result && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid="broadcast-result">
              {[
                [isFR ? 'Élèves ciblés' : 'Targeted', result.recipients, 'text-[#0F172A]'],
                [isFR ? 'SMS envoyés' : 'SMS sent', result.sms_sent, 'text-emerald-600'],
                [isFR ? 'SMS échoués' : 'SMS failed', result.sms_failed, 'text-red-600'],
                [isFR ? 'Push envoyés' : 'Push sent', result.push_sent, 'text-indigo-600'],
              ].map(([l, v, c]) => (
                <div key={l} className="p-2 rounded-md border border-[#E2E8F0]">
                  <div className={`text-xl font-outfit font-bold ${c}`}>{v}</div>
                  <div className="text-[11px] text-[#64748B]">{l}</div>
                </div>
              ))}
              {result.no_phone > 0 && (
                <div className="col-span-full text-xs text-amber-700">{result.no_phone} {isFR ? 'élève(s) sans numéro parent' : 'student(s) without parent phone'}</div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="broadcast-cancel">{isFR ? 'Fermer' : 'Close'}</Button>
          <Button onClick={send} disabled={!canSend || sending} data-testid="broadcast-send" className="bg-[#0052FF] hover:bg-[#0041CC] text-white">
            {scheduleOn ? <Clock size={16} className="mr-1.5" /> : <PaperPlaneTilt size={16} className="mr-1.5" />}
            {sending ? (isFR ? 'Envoi…' : 'Sending…')
              : scheduleOn ? (isFR ? `Programmer pour ${audience.students} famille(s)` : `Schedule for ${audience.students} families`)
                : (isFR ? `Envoyer à ${audience.students} famille(s)` : `Send to ${audience.students} families`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SmsBroadcastDialog;
